import { useState } from 'react';
import PropTypes from 'prop-types';
// material
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography
} from '@mui/material';
// components
import AddressAutocomplete from '../../components/address/AddressAutocomplete';
// services
import webServices from '../../services/webServices';
import { throwIfRequestFailed } from '../../services';

// "Add a property" (card 194e, the other half of the switcher).
//
// Talks to webServices.createAddress DIRECTLY rather than going through the
// residence redux thunk. That thunk exists to manage THE address on the
// Residence page — on success it dispatches getAddress(), which would repoint
// that page's state at the property just added rather than the one the user is
// actually looking at. It also swallows failures: its hasError dispatch is
// commented out and it returns a bare `false`, so the caller has no message to
// show. Neither behaviour is wanted here.
//
// The backend already does the rest: POST /address creates a genuine second
// address row and links it as a home without making it active, because adding a
// property should not move the homeowner off the one they were looking at.

const EMPTY = { address: '', city: '', state: '', zipCode: '', isVerified: false };

AddHomeDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func
};

export default function AddHomeDialog({ open, onClose }) {
  const [values, setValues] = useState(EMPTY);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const setField = (field) => (event) => setValues((prev) => ({ ...prev, [field]: event.target.value }));

  const handleClose = () => {
    if (saving) return;
    setValues(EMPTY);
    setError('');
    onClose();
  };

  const handleSave = async () => {
    setError('');
    if (!values.address.trim() || !values.city.trim() || !values.state.trim() || !values.zipCode.trim()) {
      setError('Street address, city, state and ZIP are all needed.');
      return;
    }

    setSaving(true);
    try {
      const resp = await webServices.createAddress(values);

      // Card 108: the address already exists and a previous owner left items
      // with the residence. That flow has its own import table and cannot be
      // completed from this dialog, so hand the person to it rather than
      // silently claiming the property with nothing imported.
      if (resp && resp.moveImport === true) {
        setSaving(false);
        setError(
          'Someone lived here before and left items with the residence. Add this one from Residence so you can choose what to import.'
        );
        return;
      }

      throwIfRequestFailed(resp);

      // Full reload for the same reason the switcher does one: the homes list,
      // and the switcher's very existence, are decided by data fetched at mount.
      window.location.reload();
    } catch (err) {
      setSaving(false);
      setError(err.message || 'Could not add the property. Please try again.');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Add a property</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          This creates a second home on your account. You will stay on the property you are looking at now — use the
          switcher in the top bar to move between them.
        </Typography>

        <Stack spacing={2.5}>
          <AddressAutocomplete
            label="Street address"
            value={values.address}
            disabled={saving}
            onInputChange={(v) => setValues((prev) => ({ ...prev, address: v }))}
            onSelect={({ address, city, state, zipCode, isVerified }) =>
              setValues((prev) => ({
                ...prev,
                address: address !== undefined ? address : prev.address,
                city: city || prev.city,
                state: state || prev.state,
                zipCode: zipCode || prev.zipCode,
                isVerified: Boolean(isVerified)
              }))
            }
          />
          <TextField fullWidth label="City" value={values.city} onChange={setField('city')} disabled={saving} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField fullWidth label="State" value={values.state} onChange={setField('state')} disabled={saving} />
            <TextField
              fullWidth
              label="ZIP code"
              value={values.zipCode}
              onChange={setField('zipCode')}
              disabled={saving}
            />
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Adding…' : 'Add property'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
